import React, { Component, Ref } from 'react';
import { useEffect, useState } from "react";
import Presenter from './Presenter';
import { IContainerProps, IFormData } from './types';
import { useDispatch, useSelector } from "react-redux";
import actions from '../../state/actions';
import selectors from "../../state/selectors";
import { IAppState } from "../../state/types";
import { useHistory, useLocation } from "react-router-dom";
import useUpdateEffect from '../../utils/CustomHooks/useUpdateEffect';
import { LOGIN_STEPS } from '../../utils/constants';
import { getEmailValidationIcon, getPasswordValidationIcon } from '../../utils/validation';

const LoginContainer = (props: IContainerProps) => {
    const [formData, setFormData] = useState<IFormData>(defaultFormValue);
    const [loaderVisible, setLoaderVisible] = useState(false);
    const dispatch = useDispatch()
    const [errorMessage, showErrorMessage] = useState(false);
    const [passwordDisabled, setPasswordDisabled] = useState(false);
    const [emailDisabled, setEmailDisabled] = useState(false);
    const [emailIcon, setEmailIcon] = useState('');
    const [passwordIcon, setPasswordIcon] = useState('');
    const [resetPassword1Icon, setResetPassword1Icon] = useState('');
    const [resetPassword2Icon, setResetPassword2Icon] = useState('');
    const [emailForgotPasswordIcon, setEmailForgotPasswordIcon] = useState('');
    const [pageTitle, setPageTitle] = useState('Iniciar Sesión');
    const [currentStep, setCurrentStep] = useState(LOGIN_STEPS.STEP_1);
    const changeStep = (step: LOGIN_STEPS) => { setCurrentStep(step) };
    const history = useHistory();
    const location = useLocation<{ from?: { pathname: string; state?: any } | string }>();
    const [verificationCode, setVerificationCode] = useState<string[]>([]);
    const [sendCodeDisabledBtn, setSendCodeDisabledBtn] = useState(true);
    const [messageError, setMessageError] = useState(false);
    const [messageErrorStep2, setMessageErrorStep2] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState(false);
    const [passwordsNotMatchErrorMsg, setPasswordsNotMatchErrorMsg] = useState(false);
    const [codeBtnDisabled, setCodeBtnDisabled] = useState(true);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const authUser = useSelector((state: IAppState) => selectors.auth.getAuthUser(state));
    const rehydrated = useSelector((state: IAppState) => selectors.auth.getRehydrated(state));

    // Extract destination path as stable primitive
    const fromPath = location.state?.from || '/';

    const onChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
        showErrorMessage(false);
        let inputData = e.target.value;
        setFormData({ ...formData, [type]: inputData });

        if (type === 'email') {
            setEmailIcon(getEmailValidationIcon(inputData));
        }
        if (type === 'emailForgotPassword') {
            setEmailForgotPasswordIcon(getEmailValidationIcon(inputData));
            setMessageErrorStep2(false);
        };
        if (type === 'password') {
            setPasswordIcon(getPasswordValidationIcon(inputData));
        }
    };

    const actionBtn = () => {
        switch (currentStep) {
            case 1:
                if (!validateData()) return
                resetInputs();
                setLoaderVisible(true);
                dispatch(actions.auth.loginRequest(formData.email.toLowerCase(), formData.password, resultSignIn));
                break;
            case 2:
                if (emailForgotPasswordIcon === 'done') {
                    setLoaderVisible(true);
                    // dispatch(actions.api.forgotPasswordStep1(formData.emailForgotPassword, resultsForgotPasswordStep1));
                }
                break;
            case 3:
                setLoaderVisible(true);
                // dispatch(actions.api.forgotPasswordStep2(formData.emailForgotPassword, verificationCode.join(''), resultValidateCode));
                break;
            case 4:
                validatePassword();
                if (resetPassword1Icon === 'done' && resetPassword2Icon === 'done') {
                    setLoaderVisible(true);
                    // dispatch(actions.api.forgotPasswordStep3(formData.emailForgotPassword, verificationCode.join(''),
                    // formData.resetPassword1, resultResetPassword));
                }
                break;
            default:
                break;
        }
    }

    const resultSignIn = (result: any, error: any) => {
        setLoaderVisible(false);
        setPasswordDisabled(false);
        setEmailDisabled(false);

        if (error) {
            showErrorMessage(true);
            setPasswordIcon('alert');
        } else {
            // Explicitly trigger navigation on success
            setShouldNavigate(true);
        }
    }

    const resultsForgotPasswordStep1 = (result: any, error: any) => {
        setLoaderVisible(false);
        if (result.data && !result.data.error) {
            changeStep(LOGIN_STEPS.STEP_3);
        } else {
            setMessageErrorStep2(true);
        }
    }

    const resultValidateCode = (response: any, error: any) => {
        setLoaderVisible(false);
        if (response.data && !response.data.error) {
            changeStep(LOGIN_STEPS.STEP_4);
        } else {
            setMessageError(true);
            setCodeBtnDisabled(true);
        }
    }

    const resultResetPassword = (result: any, error: any) => {
        setLoaderVisible(false);
        if (result.data && !result.data.error) {
            changeStep(LOGIN_STEPS.STEP_1);
        }
    }

    const validateData = () => {
        let isFormValid = true;
        if (!formData.email) {
            setEmailIcon('alert');
            isFormValid = false;
        }
        if (!formData.password) {
            setPasswordIcon('alert');
            isFormValid = false;
        }
        return isFormValid;
    }

    // Validation functions moved to utils/validation.ts

    function defaultFormValue() {
        return {
            email: '',
            password: '',
            emailForgotPassword: '',
            resetPassword1: '',
            resetPassword2: ''
        };
    }

    const resetInputs = () => {
        setEmailIcon('done');
        setPasswordIcon('done');
        showErrorMessage(false);
        setPasswordDisabled(true);
        setEmailDisabled(true);
    }

    const onCodeChange = (code: any) => {
        setVerificationCode(code.toUpperCase().split(''));
        setMessageError(false);
        if (code.length > 4) {
            setCodeBtnDisabled(false);
        } else {
            setCodeBtnDisabled(true);
        }
    };

    const verificationCodeRef = React.useRef<any>();

    const clearVerificationCode = () => {
        verificationCodeRef && verificationCodeRef.current.__clearvalues__();
    };

    const deleteInputText = () => {
        setFormData({ ...formData, emailForgotPassword: '' });
    };

    const validatePassword = () => {
        const passwordRegex = new RegExp(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/);
        if (!passwordRegex.test(formData.resetPassword1)) {
            setResetPassword1Icon('alert');
            setResetPasswordError(true);
        } else {
            setResetPassword1Icon('done');
            setResetPasswordError(false);
        }

        if (formData.resetPassword1.length === 0) {
            setResetPassword2Icon('');
            setResetPasswordError(true);
        }
        else if (formData.resetPassword1 !== formData.resetPassword2) {
            if (resetPassword1Icon === 'done') {
                setResetPassword2Icon('alert');
                setPasswordsNotMatchErrorMsg(true);
            } else {
                setResetPassword2Icon('');
                setPasswordsNotMatchErrorMsg(false);
            }
        } else {
            setResetPassword2Icon('done');
            setResetPasswordError(false);
            setPasswordsNotMatchErrorMsg(false);
        }
    }

    useUpdateEffect(() => {
        if (currentStep === 1) {
            setFormData(defaultFormValue);
        }
    }, [currentStep]);

    // Updates everytime step changes
    useEffect(() => {
        switch (currentStep) {
            case 1:
                setPageTitle('Iniciar Sesión');
                setFormData(defaultFormValue);
                setResetPassword1Icon('');
                setResetPassword2Icon('');
                setResetPasswordError(false);
                setPasswordsNotMatchErrorMsg(false);
                setEmailForgotPasswordIcon('');
                setMessageError(false);
                break;
            case (2):
                setPageTitle('Forgot Password');
                break;
            case (3):
                setPageTitle('Forgot Password');
                clearVerificationCode();
                break;
            case 4:
                setPageTitle('Reset Password');
                setResetPassword1Icon('');
                setResetPassword2Icon('');
                break;
            default:
                break;
        }
    }, [currentStep]);

    // WHEN THERE IS AN UPDATE ON THE RESET PASSWORDS GO TO VALIDATION
    useUpdateEffect(() => {
        validatePassword()
    }, [formData.resetPassword1, formData.resetPassword2]);

    // DISABLE/ENABLE SEND CODE BUTTON
    useUpdateEffect(() => {
        if (emailForgotPasswordIcon === 'done') {
            setSendCodeDisabledBtn(false);
        }
        else {
            setSendCodeDisabledBtn(true);
        }
    }, [formData.emailForgotPassword]);

    // Navigate ONLY when explicitly triggered by successful login
    useEffect(() => {
        if (shouldNavigate && authUser && rehydrated) {
            history.replace(fromPath);
            // No need to reset flag - component will unmount after navigation
        }
    }, [shouldNavigate, authUser, rehydrated, fromPath, history]);

    // useEffect(() => {
    //     document.title = "Log in";
    // }, []);

    return (
        <Presenter
            {...props}
            formData={formData}
            loaderVisible={loaderVisible}
            onChange={onChange}
            actionBtn={actionBtn}
            errorMessage={errorMessage}
            emailIcon={emailIcon}
            passwordIcon={passwordIcon}
            passwordDisabled={passwordDisabled}
            emailDisabled={emailDisabled}
            pageTitle={pageTitle}
            currentStep={currentStep}
            changeStep={changeStep}
            verificationCode={verificationCode}
            verificationCodeRef={verificationCodeRef}
            emailForgotPasswordIcon={emailForgotPasswordIcon}
            onCodeChange={onCodeChange}
            messageError={messageError}
            messageErrorStep2={messageErrorStep2}
            codeBtnDisabled={codeBtnDisabled}
            resetPassword1Icon={resetPassword1Icon}
            resetPassword2Icon={resetPassword2Icon}
            resetPasswordError={resetPasswordError}
            passwordsNotMatchErrorMsg={passwordsNotMatchErrorMsg}
            sendCodeDisabledBtn={sendCodeDisabledBtn}
            deleteInputText={deleteInputText}
        />
    );
};

export default LoginContainer;
